import type { Pinia } from 'pinia'
import { store } from '@/store'
import { setSdkApiConfig } from '@/microfrontends/monitor-mfe/sdk/sdk-api-config'
import { setSdkAuth, type SdkAuthOptions } from '@/microfrontends/monitor-mfe/sdk/sdk-auth'
import {
  hydrateOrganizationFromStorage,
  initOsdVisibleFromContext,
} from '@/microfrontends/monitor-mfe/sdk/sdk-monitor-bootstrap'

export interface SdkExternalContext {
  /** 等价于 sessionStorage.token，挂载前写入 */
  token?: string
  sessionStorage?: Record<string, string>
  localStorage?: Record<string, string>
  dockSn?: string
  uavObj?: Record<string, unknown>
  organizationData?: {
    cityCode?: string
    areaCode?: string
    provinceCode?: string
  }
  clientId?: string
  deviceState?: {
    currentSn?: string
    currentType?: number
    dockInfo?: Record<string, unknown>
    deviceInfo?: Record<string, unknown>
    gatewayInfo?: Record<string, unknown>
  }
  osdVisible?: Record<string, unknown>
  treatActive?: boolean
  airValueActive?: number
  payloadIndex?: string
  videoId?: string
  enableDeviceWebSocket?: boolean
  deviceWebSocketUrl?: string
  /** axios 请求根地址，外部嵌入必填，如 https://fh.zxhkuav.cn */
  apiBaseUrl?: string
  /** 识别流等用到的业务根地址，默认同 apiBaseUrl 对应环境 */
  apiBaseUrlFull?: string
  detectImageApi?: string
}

let sharedDefaults: SdkExternalContext = {}

export function setSdkDefaults (ctx: SdkExternalContext): void {
  sharedDefaults = mergeSdkExternalContext(sharedDefaults, ctx)
}

export function clearSdkDefaults (): void {
  sharedDefaults = {}
}

export function mergeSdkExternalContext (
  base: SdkExternalContext,
  extra?: SdkExternalContext
): SdkExternalContext {
  if (!extra) return { ...base }
  const o: SdkExternalContext = { ...base, ...extra }
  if (base.localStorage || extra.localStorage) {
    o.localStorage = { ...base.localStorage, ...extra.localStorage }
  }
  if (base.sessionStorage || extra.sessionStorage) {
    o.sessionStorage = { ...base.sessionStorage, ...extra.sessionStorage }
  }
  if (base.organizationData || extra.organizationData) {
    o.organizationData = { ...base.organizationData, ...extra.organizationData }
  }
  if (base.osdVisible || extra.osdVisible) {
    o.osdVisible = { ...base.osdVisible, ...extra.osdVisible }
  }
  if (base.deviceState || extra.deviceState) {
    const bd = base.deviceState
    const ed = extra.deviceState
    o.deviceState = {
      ...bd,
      ...ed,
      dockInfo: { ...bd?.dockInfo, ...ed?.dockInfo },
      deviceInfo: { ...bd?.deviceInfo, ...ed?.deviceInfo },
      gatewayInfo: { ...bd?.gatewayInfo, ...ed?.gatewayInfo },
    }
  }
  if (extra.uavObj !== undefined) o.uavObj = extra.uavObj
  else if (base.uavObj !== undefined) o.uavObj = base.uavObj
  if (extra.dockSn !== undefined) o.dockSn = extra.dockSn
  else if (base.dockSn !== undefined) o.dockSn = base.dockSn
  if (extra.clientId !== undefined) o.clientId = extra.clientId
  else if (base.clientId !== undefined) o.clientId = base.clientId
  return o
}

export function getMergedSdkContext (mountContext?: SdkExternalContext): SdkExternalContext | undefined {
  const m = mergeSdkExternalContext(sharedDefaults, mountContext)
  if (
    !m.localStorage &&
    !m.sessionStorage &&
    !m.token?.trim() &&
    m.dockSn == null &&
    m.uavObj == null &&
    !m.organizationData &&
    m.clientId == null &&
    !m.deviceState &&
    !m.osdVisible &&
    m.treatActive == null &&
    m.airValueActive == null &&
    m.payloadIndex == null &&
    m.videoId == null &&
    m.enableDeviceWebSocket !== true &&
    !(m.deviceWebSocketUrl && String(m.deviceWebSocketUrl).trim()) &&
    !(m.apiBaseUrl && String(m.apiBaseUrl).trim()) &&
    !(m.apiBaseUrlFull && String(m.apiBaseUrlFull).trim()) &&
    !(m.detectImageApi && String(m.detectImageApi).trim())
  ) {
    return undefined
  }
  return m
}

export function applySdkApiFromContext (ctx: SdkExternalContext | undefined): void {
  if (!ctx) return
  const patch: Parameters<typeof setSdkApiConfig>[0] = {}
  if (ctx.apiBaseUrl?.trim()) patch.baseApi = ctx.apiBaseUrl.trim()
  if (ctx.apiBaseUrlFull?.trim()) patch.baseUrl = ctx.apiBaseUrlFull.trim()
  if (ctx.detectImageApi?.trim()) patch.detectImageApi = ctx.detectImageApi.trim()
  if (Object.keys(patch).length) setSdkApiConfig(patch)
}

export function applySdkExternalContext (pinia: Pinia, ctx: SdkExternalContext | undefined): void {
  if (ctx) {
    applySdkApiFromContext(ctx)

    const auth: SdkAuthOptions = {}
    if (ctx.token?.trim()) auth.token = ctx.token.trim()
    if (ctx.sessionStorage) auth.sessionStorage = ctx.sessionStorage
    if (ctx.localStorage?.Workspace_id) auth.workspaceId = ctx.localStorage.Workspace_id
    if (Object.keys(auth).length) setSdkAuth(auth)

    if (ctx.localStorage) {
      for (const [k, v] of Object.entries(ctx.localStorage)) {
        try {
          localStorage.setItem(k, v)
        } catch {
          /* ignore */
        }
      }
    }
  }

  hydrateOrganizationFromStorage(pinia, ctx)
  if (!ctx) return

  const info = store.informationListStore.informationStore(pinia)

  if (ctx.dockSn != null && ctx.dockSn !== '') {
    void info.updateInputValue(ctx.dockSn)
  }
  if (ctx.uavObj != null && Object.keys(ctx.uavObj).length > 0) {
    void info.setUavObj(ctx.uavObj)
  }
  if (ctx.organizationData != null && Object.keys(ctx.organizationData).length > 0) {
    void info.setOrganizationData({
      ...info.organizationData,
      ...ctx.organizationData,
    })
  }
  if (ctx.payloadIndex != null) void info.setPayloadIndex(ctx.payloadIndex)
  if (ctx.videoId != null) void info.setVideoId(ctx.videoId)
  if (ctx.treatActive != null) void info.setTreatActive(ctx.treatActive)
  if (ctx.airValueActive != null) void info.setAirValueActive(ctx.airValueActive)

  const my = store.other.useMyStore(pinia)

  if (ctx.clientId != null) {
    my.$patch({ clientId: ctx.clientId })
  }

  if (ctx.deviceState) {
    const ds = ctx.deviceState
    my.$patch((s) => {
      if (ds.currentSn != null) s.deviceState.currentSn = ds.currentSn
      if (ds.currentType != null) s.deviceState.currentType = ds.currentType
      if (ds.dockInfo) {
        s.deviceState.dockInfo = { ...s.deviceState.dockInfo, ...ds.dockInfo } as typeof s.deviceState.dockInfo
      }
      if (ds.deviceInfo) {
        s.deviceState.deviceInfo = { ...s.deviceState.deviceInfo, ...ds.deviceInfo } as typeof s.deviceState.deviceInfo
      }
      if (ds.gatewayInfo) {
        s.deviceState.gatewayInfo = {
          ...s.deviceState.gatewayInfo,
          ...ds.gatewayInfo,
        } as typeof s.deviceState.gatewayInfo
      }
    })
  }

  if (ctx.osdVisible && Object.keys(ctx.osdVisible).length > 0) {
    my.$patch((s) => {
      Object.assign(s.osdVisible, ctx.osdVisible)
    })
  }

  initOsdVisibleFromContext(pinia, ctx)
}
